import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X } from "lucide-react";
import { attachmentPreviewKind } from "@mk/core";
import type { Attachment } from "../../../server/src/models";
import { api } from "../api";
import { IconButton } from "./ui";

export function AttachmentPreview({
  attachment,
  onClose,
}: {
  attachment: Attachment;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const media = useRef<HTMLMediaElement>(null);
  const heading = useId();
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState("");
  const kind = attachmentPreviewKind(attachment.mime);
  const url = `/v1/attachments/${attachment.id}`;
  const mediaError =
    "This browser cannot play this file, or the preview could not be loaded. Download it to open in another player.";

  useEffect(() => {
    const element = dialog.current!;
    const player = media.current;
    const opener = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    element.showModal();
    return () => {
      if (player) {
        player.pause();
        player.removeAttribute("src");
        player.load();
      }
      element.close();
      document.body.style.overflow = overflow;
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (kind === "text") {
      void api<{ text: string; truncated: boolean }>(`${url}/preview`)
        .then((p) => {
          if (!cancelled)
            setText(
              p.text +
                (p.truncated
                  ? "\n\n[Preview truncated — download the full file.]"
                  : ""),
            );
        })
        .catch(() => {
          if (!cancelled)
            setError("No preview available. Download this file to open it.");
        });
    } else if (media.current) {
      if (!media.current.canPlayType(attachment.mime)) setError(mediaError);
      else media.current.src = `${url}?inline=1`;
    }
    return () => {
      cancelled = true;
    };
  }, [attachment.mime, kind, url]);

  return createPortal(
    <dialog
      ref={dialog}
      className="file-preview"
      aria-labelledby={heading}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="section-heading">
        <h2 id={heading}>{attachment.filename}</h2>
        <a className="button small" href={url} download>
          <Download size={13} />
          Download
        </a>
        <IconButton label="Close preview" onClick={onClose}>
          <X size={18} />
        </IconButton>
      </div>
      {error ? (
        <p role="status">{error}</p>
      ) : kind === "video" ? (
        <video
          ref={media as React.RefObject<HTMLVideoElement>}
          controls
          playsInline
          preload="metadata"
          aria-label={attachment.filename}
          onError={() => setError(mediaError)}
        />
      ) : kind === "audio" ? (
        <audio
          ref={media as React.RefObject<HTMLAudioElement>}
          controls
          preload="metadata"
          aria-label={attachment.filename}
          onError={() => setError(mediaError)}
        />
      ) : kind === "image" ? (
        <img
          src={`${url}?inline=1`}
          alt={attachment.caption || attachment.filename}
          onError={() =>
            setError(
              "The image preview could not be loaded. Download this file to open it.",
            )
          }
        />
      ) : kind === "pdf" ? (
        <>
          <p className="subtle">
            If your browser cannot display this PDF, use Download to open it.
          </p>
          <iframe
            title={attachment.filename}
            src={`${url}?inline=1`}
            onError={() =>
              setError(
                "The PDF preview could not be loaded. Download this file to open it.",
              )
            }
          />
        </>
      ) : (
        <pre>{text ?? "Loading preview…"}</pre>
      )}
    </dialog>,
    document.body,
  );
}
